import { Fragment } from "react";
import {
  Check,
  Pencil,
  Plus,
  ArrowLeftRight,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import type {
  TaxonomyFamily,
  FamilyMemberSelection,
} from "../../types/taxonomy";
import type { GateFamilyState } from "../../utils/pipeline-groups";

interface BinaryFamilyControlProps {
  family: TaxonomyFamily;
  /**
   * Saved member index. `null` = the model made no prediction for this
   * family (per-image add mode), `undefined` = not applicable (bulk bar).
   */
  currentIndex?: number | null;
  selectedIndex: FamilyMemberSelection;
  disabled?: boolean;
  caption?: React.ReactNode;
  staged?: boolean;
  corrected?: boolean;
  /** Saved correction is stale — the model re-ran since (amber, no glow). */
  reviewOutdated?: boolean;
  /** Label was added manually by a reviewer (violet). */
  humanAdded?: boolean;
  /** Member indexes in display order (defaults to natural order). */
  displayOrder?: number[];
  /** Quality-gate dot next to the family name (pass/fail/unknown). */
  gateDot?: GateFamilyState;
  /** Shortcut key displayed on the control (e.g. "b", "c", "1"). */
  shortcutKey?: string;
  onChange: (selection: FamilyMemberSelection) => void;
  /** Inline error message from a failed 422 response */
  errorMessage?: string | null;
}

/** Gate dot states are internal values, mapped to display text at the edge. */
const GATE_STATE_KEYS: Record<
  GateFamilyState,
  "qualityGate.pass" | "qualityGate.fail" | "qualityGate.unknown"
> = {
  pass: "qualityGate.pass",
  fail: "qualityGate.fail",
  unknown: "qualityGate.unknown",
};

/**
 * Classification family control — N-member toggle.
 *
 * Design: large side-by-side buttons, one per `family.members[]` entry
 * (rendered in `displayOrder`, canonical indexes unchanged). The active
 * member has a solid fill coloured by review state:
 *
 *   model only        zinc           ·  corrected        emerald + Check
 *   outdated          amber + Alert  ·  human added      violet + Plus
 *   staged correction amber + Pencil + glow
 *   staged creation   violet + Plus + glow
 *
 * Add mode (no prediction, nothing staged) renders a dashed border.
 * When `shortcutKey` is set, shows the key badge with a "cycle"
 * affordance — pressing it advances members[] → none.
 */
const BinaryFamilyControl = ({
  family,
  currentIndex,
  selectedIndex,
  disabled = false,
  caption,
  staged = false,
  corrected = false,
  reviewOutdated = false,
  humanAdded = false,
  displayOrder,
  gateDot,
  shortcutKey,
  onChange,
  errorMessage,
}: BinaryFamilyControlProps) => {
  const { t } = useTranslation("mlExports");
  // Staged creation = staging a label where the model made no prediction.
  const stagedCreation = staged && currentIndex === null;
  const stagedCorrection = staged && !stagedCreation;
  const addMode = currentIndex === null && !staged && !humanAdded;

  const handleClick = (index: number) => {
    if (disabled) return;
    onChange(index);
  };

  const renderMember = (index: number) => {
    const member = family.members[index];
    const isSelected = selectedIndex === index;
    const isCurrent = currentIndex === index;

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => handleClick(index)}
        className={cn(
          "flex-1 relative px-3 py-1.5 text-xs font-medium transition-all duration-150",
          "disabled:cursor-not-allowed",
          // States
          !isSelected && !disabled &&
            "text-muted-foreground hover:bg-accent/80 hover:text-foreground",
          !isSelected && disabled && "text-muted-foreground/50",
          // Selected fill by review state (staged wins, then saved states)
          isSelected && "text-white font-semibold",
          isSelected && stagedCreation &&
            "bg-violet-500 shadow-[0_0_12px_rgba(139,92,246,0.4)]",
          isSelected && stagedCorrection &&
            "bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.3)]",
          isSelected && !staged && corrected && "bg-emerald-500",
          isSelected && !staged && !corrected && humanAdded && "bg-violet-600",
          isSelected && !staged && !corrected && !humanAdded &&
            reviewOutdated && "bg-amber-500",
          isSelected && !staged && !corrected && !humanAdded &&
            !reviewOutdated && "bg-zinc-500",
        )}
      >
        <span className="inline-flex items-center justify-center gap-1.5">
          {isSelected && stagedCorrection && <Pencil className="h-3.5 w-3.5" />}
          {isSelected && stagedCreation && <Plus className="h-3.5 w-3.5" />}
          {isSelected && !staged && corrected && (
            <Check className="h-3.5 w-3.5" />
          )}
          {isSelected && !staged && !corrected && humanAdded && (
            <Plus className="h-3.5 w-3.5" />
          )}
          {isSelected && !staged && !corrected && !humanAdded &&
            reviewOutdated && <AlertTriangle className="h-3.5 w-3.5" />}
          {isCurrent && !isSelected && (
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-40" />
          )}
          {member.display_name}
        </span>

        {/* Confidence micro-bar when this is the model's pick */}
        {isCurrent && !isSelected && (
          <span className="absolute bottom-1 inset-x-3 h-[2px] rounded-full bg-muted-foreground/15" />
        )}
      </button>
    );
  };

  const memberOrder = displayOrder ?? family.members.map((_, i) => i);

  return (
    <div className="space-y-1.5">
      {/* Family header with gate dot + shortcut key */}
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
          {gateDot && (
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full shrink-0",
                gateDot === "pass" && "bg-emerald-500",
                gateDot === "fail" && "bg-amber-500",
                gateDot === "unknown" && "bg-zinc-400",
              )}
              title={t("qualityGate.dotTitle", {
                state: t(GATE_STATE_KEYS[gateDot]),
              })}
            />
          )}
          {family.display_name}
        </span>
        {shortcutKey && (
          <span className={cn(
            "inline-flex items-center gap-1 text-[10px] text-muted-foreground",
            disabled && "opacity-40",
          )}>
            <kbd className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded bg-muted border border-border text-[10px] font-mono font-semibold uppercase">
              {shortcutKey}
            </kbd>
            {!disabled && (
              <>
                <ArrowLeftRight className="h-2.5 w-2.5 opacity-60" />
                <span className="opacity-60">{t("familyControl.cycle")}</span>
              </>
            )}
          </span>
        )}
      </div>

      {/* Member toggle — one button per member, in display order */}
      <div
        className={cn(
          "flex items-stretch rounded-md border bg-background overflow-hidden",
          disabled && "opacity-50",
          addMode && "border-dashed",
          stagedCreation &&
            "border-violet-500/60 shadow-[0_0_12px_rgba(139,92,246,0.25)]",
          stagedCorrection &&
            "border-amber-500/50 shadow-[0_0_12px_rgba(245,158,11,0.2)]",
          !staged && corrected && "border-emerald-500/50",
          !staged && !corrected && humanAdded && "border-violet-500/50",
          !staged && !corrected && !humanAdded && reviewOutdated &&
            "border-amber-500/50",
        )}
      >
        {memberOrder.map((index, position) => (
          <Fragment key={family.members[index].canonical}>
            {position > 0 && <div className="w-px bg-border" />}
            {renderMember(index)}
          </Fragment>
        ))}
      </div>

      {/* Caption */}
      {caption && (
        <div className="text-[11px] text-muted-foreground pl-0.5">
          {caption}
        </div>
      )}
      {errorMessage && (
        <div className="flex items-center gap-1 text-[11px] text-red-500 pl-0.5">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default BinaryFamilyControl;
